import { useMemo, useState } from 'react';
import { type ColumnDef } from '@tanstack/react-table';
import { Plus, Pencil, Trash2, Layers, GripVertical, ArrowUp, ArrowDown } from 'lucide-react';
import { format } from 'date-fns';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DataTable } from '@/components/shared/DataTable';
import { EmptyState } from '@/components/shared/EmptyState';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { useToast } from '@/components/shared/Toast';
import {
  useGetAgentModelCollectionsQuery,
  useGetAgentModelsQuery,
  useCreateAgentModelCollectionMutation,
  useUpdateAgentModelCollectionMutation,
  useDeleteAgentModelCollectionMutation,
} from '@/store/api/agentFactoryApi';
import type { AgentModelCollection, ModelCollectionStrategy } from '@/types';

interface EntryRow {
  key: string;
  modelConfigId: string;
  weight: string;
  position: string;
}

interface CollectionFormState {
  name: string;
  description: string;
  strategy: ModelCollectionStrategy;
  failoverEnabled: boolean;
  maxAttemptsPerRun: string;
  entries: EntryRow[];
}

let entryKey = 0;
const newEntry = (modelConfigId = ''): EntryRow => ({
  key: `entry-${++entryKey}`,
  modelConfigId,
  weight: '',
  position: '',
});

const emptyForm: CollectionFormState = {
  name: '',
  description: '',
  strategy: 'position',
  failoverEnabled: true,
  maxAttemptsPerRun: '',
  entries: [newEntry()],
};

function toNum(raw: string): number | undefined {
  if (raw.trim() === '') return undefined;
  const n = Number(raw);
  return Number.isFinite(n) ? n : undefined;
}

function fromCollection(c: AgentModelCollection): CollectionFormState {
  return {
    name: c.name,
    description: c.description ?? '',
    strategy: c.strategy,
    failoverEnabled: c.failover?.enabled ?? true,
    maxAttemptsPerRun: c.failover?.maxAttemptsPerRun != null ? String(c.failover.maxAttemptsPerRun) : '',
    entries:
      c.entries.length > 0
        ? c.entries.map((e) => ({
            key: `entry-${++entryKey}`,
            modelConfigId: e.modelConfigId,
            weight: e.weight != null ? String(e.weight) : '',
            position: e.position != null ? String(e.position) : '',
          }))
        : [newEntry()],
  };
}

export function ModelCollectionsPage() {
  const { success: toastSuccess, error: toastError } = useToast();

  const { data: collections, isLoading, isError } = useGetAgentModelCollectionsQuery();
  const { data: models } = useGetAgentModelsQuery();

  const [createCollection, { isLoading: creating }] = useCreateAgentModelCollectionMutation();
  const [updateCollection, { isLoading: updating }] = useUpdateAgentModelCollectionMutation();
  const [deleteCollection, { isLoading: deleting }] = useDeleteAgentModelCollectionMutation();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<CollectionFormState>(emptyForm);
  const [deleteTarget, setDeleteTarget] = useState<AgentModelCollection | null>(null);

  const isSubmitting = creating || updating;

  const setEntry = (key: string, patch: Partial<EntryRow>) =>
    setForm((prev) => ({
      ...prev,
      entries: prev.entries.map((e) => (e.key === key ? { ...e, ...patch } : e)),
    }));

  const removeEntry = (key: string) =>
    setForm((prev) => ({ ...prev, entries: prev.entries.filter((e) => e.key !== key) }));

  const moveEntry = (index: number, dir: -1 | 1) =>
    setForm((prev) => {
      const next = [...prev.entries];
      const target = index + dir;
      if (target < 0 || target >= next.length) return prev;
      [next[index], next[target]] = [next[target], next[index]];
      return { ...prev, entries: next };
    });

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (c: AgentModelCollection) => {
    setEditingId(c._id);
    setForm(fromCollection(c));
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!form.name.trim()) return;
    const dto = {
      name: form.name.trim(),
      description: form.description.trim() || undefined,
      strategy: form.strategy,
      entries: form.entries
        .filter((e) => e.modelConfigId)
        .map((e) => ({
          modelConfigId: e.modelConfigId,
          weight: e.weight ? toNum(e.weight) : undefined,
          position: e.position ? toNum(e.position) : undefined,
        })),
      failover: {
        enabled: form.failoverEnabled,
        maxAttemptsPerRun: toNum(form.maxAttemptsPerRun),
      },
    };
    try {
      if (editingId) {
        await updateCollection({ id: editingId, body: dto }).unwrap();
        toastSuccess('Collection updated');
      } else {
        await createCollection(dto).unwrap();
        toastSuccess('Collection created');
      }
      setDialogOpen(false);
    } catch (err) {
      toastError((err as any)?.data?.message || 'Failed to save collection');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteCollection(deleteTarget._id).unwrap();
      toastSuccess('Collection deleted');
      setDeleteTarget(null);
    } catch {
      toastError('Failed to delete collection');
    }
  };

  const columns: ColumnDef<AgentModelCollection, unknown>[] = useMemo(
    () => [
      {
        accessorKey: 'name',
        header: 'Name',
        cell: ({ row }) => (
          <div className="flex items-center gap-2">
            <Layers className="h-4 w-4 shrink-0 text-muted-foreground" />
            <div className="min-w-0">
              <p className="truncate font-medium">{row.original.name}</p>
              {row.original.description && (
                <p className="truncate text-xs text-muted-foreground">{row.original.description}</p>
              )}
            </div>
          </div>
        ),
      },
      {
        accessorKey: 'strategy',
        header: 'Strategy',
        cell: ({ row }) => <Badge variant="outline">{row.original.strategy}</Badge>,
      },
      {
        accessorKey: 'entries',
        header: 'Entries',
        cell: ({ row }) => <span className="tabular-nums">{row.original.entries.length}</span>,
      },
      {
        accessorKey: 'failover.enabled',
        header: 'Failover',
        cell: ({ row }) => (
          <Badge variant={row.original.failover?.enabled ? 'success' : 'secondary'}>
            {row.original.failover?.enabled ? 'enabled' : 'disabled'}
          </Badge>
        ),
      },
      {
        accessorKey: 'createdAt',
        header: 'Created',
        cell: ({ row }) => format(new Date(row.original.createdAt), 'MMM d, yyyy'),
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => (
          <div className="flex justify-end gap-1">
            <Button
              variant="ghost"
              size="icon"
              title="Edit"
              onClick={(e) => {
                e.stopPropagation();
                openEdit(row.original);
              }}
            >
              <Pencil className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              title="Delete"
              onClick={(e) => {
                e.stopPropagation();
                setDeleteTarget(row.original);
              }}
            >
              <Trash2 className="h-4 w-4 text-destructive" />
            </Button>
          </div>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Model collections</h1>
          <p className="text-sm text-muted-foreground">
            Ordered/priced model sets with selection and failover strategy.
          </p>
        </div>
        <Button onClick={openCreate}>
          <Plus className="mr-2 h-4 w-4" /> New Collection
        </Button>
      </div>

      <Card>
        <CardHeader className="pb-0">
          <p className="text-sm font-medium">Model collections</p>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <LoadingSpinner className="py-12" />
          ) : isError ? (
            <p className="py-8 text-center text-sm text-destructive">Failed to load collections.</p>
          ) : !collections || collections.length === 0 ? (
            <EmptyState
              title="No collections"
              description="Group models with a selection strategy to back factories."
              action={<Button onClick={openCreate}>New Collection</Button>}
            />
          ) : (
            <DataTable columns={columns} data={collections} onRowClick={() => {}} />
          )}
        </CardContent>
      </Card>

      {/* ── Editor dialog ────────────────────────────────────────────────── */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Edit collection' : 'New collection'}</DialogTitle>
            <DialogDescription>
              Define entries with weight (0..1) or position, plus failover settings.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="mc-name">Name *</Label>
                <Input
                  id="mc-name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. Fast + cheap"
                />
              </div>
              <div className="space-y-2">
                <Label>Strategy</Label>
                <Select
                  value={form.strategy}
                  onValueChange={(v) => setForm({ ...form, strategy: v as ModelCollectionStrategy })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="position">position</SelectItem>
                    <SelectItem value="weight">weight</SelectItem>
                    <SelectItem value="random">random</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="mc-desc">Description</Label>
                <Input
                  id="mc-desc"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                />
              </div>
            </div>

            {/* Entries */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Entries</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setForm((prev) => ({ ...prev, entries: [...prev.entries, newEntry()] }))}
                >
                  <Plus className="mr-1 h-3.5 w-3.5" /> Add entry
                </Button>
              </div>
              {form.entries.length === 0 ? (
                <p className="rounded-md border border-dashed px-3 py-2 text-xs text-muted-foreground">
                  No entries — add at least one model.
                </p>
              ) : (
                <div className="space-y-2">
                  {form.entries.map((entry, index) => (
                    <div key={entry.key} className="flex flex-wrap items-end gap-2 rounded-md border p-2">
                      <GripVertical className="mb-2.5 h-4 w-4 shrink-0 text-muted-foreground" />
                      <div className="min-w-[10rem] flex-1 space-y-1">
                        <span className="text-xs text-muted-foreground">Model</span>
                        <Select
                          value={entry.modelConfigId}
                          onValueChange={(v) => setEntry(entry.key, { modelConfigId: v })}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select model" />
                          </SelectTrigger>
                          <SelectContent>
                            {(models ?? []).map((m) => (
                              <SelectItem key={m._id} value={m._id}>
                                {m.name} — {m.modelName}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="w-24 space-y-1">
                        <span className="text-xs text-muted-foreground">Weight (0..1)</span>
                        <Input
                          type="number" min={0} max={1} step={0.01}
                          value={entry.weight}
                          onChange={(e) => setEntry(entry.key, { weight: e.target.value })}
                        />
                      </div>
                      <div className="w-24 space-y-1">
                        <span className="text-xs text-muted-foreground">Position</span>
                        <Input
                          type="number" step={1}
                          value={entry.position}
                          onChange={(e) => setEntry(entry.key, { position: e.target.value })}
                        />
                      </div>
                      <div className="flex gap-1 pb-0.5">
                        <Button
                          type="button" variant="ghost" size="icon"
                          title="Move up" disabled={index === 0}
                          onClick={() => moveEntry(index, -1)}
                        >
                          <ArrowUp className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          type="button" variant="ghost" size="icon"
                          title="Move down" disabled={index === form.entries.length - 1}
                          onClick={() => moveEntry(index, 1)}
                        >
                          <ArrowDown className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          type="button" variant="ghost" size="icon"
                          title="Remove" className="text-destructive"
                          onClick={() => removeEntry(entry.key)}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Failover */}
            <div className="space-y-3 rounded-lg border p-3">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="failover-enable"
                  checked={form.failoverEnabled}
                  onChange={(e) => setForm({ ...form, failoverEnabled: e.target.checked })}
                  className="h-4 w-4 rounded border-input accent-primary"
                />
                <Label htmlFor="failover-enable" className="text-sm">Enable failover</Label>
              </div>
              {form.failoverEnabled && (
                <div className="space-y-1">
                  <span className="text-xs text-muted-foreground">Max attempts per run</span>
                  <Input
                    type="number"
                    value={form.maxAttemptsPerRun}
                    onChange={(e) => setForm({ ...form, maxAttemptsPerRun: e.target.value })}
                    placeholder="2"
                    className="max-w-[12rem]"
                  />
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={isSubmitting || !form.name.trim() || form.entries.filter((e) => e.modelConfigId).length === 0}
            >
              {isSubmitting ? 'Saving…' : editingId ? 'Save changes' : 'Create collection'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title="Delete collection"
        description={`Are you sure you want to delete "${deleteTarget?.name ?? 'this collection'}"?`}
        confirmLabel="Delete"
        variant="destructive"
        onConfirm={handleDelete}
        isLoading={deleting}
      />
    </div>
  );
}